export const ASSESSMENT_REPORT_TEST_KEY = 'ASSESSMENT_REPORT' as const;
export const LEGACY_COMPOSITE_REPORT_TEST_KEY = 'COMPOSITE_REPORT' as const;
export const ASSESSMENT_REPORT_SCHEMA = 'revahub.assessment-report' as const;
export const ASSESSMENT_REPORT_SCHEMA_VERSION = 2 as const;
export const CBDF_CLASSIFICATION_VERSION = 'CBDF-1' as const;
export const CBDF_OFFICIAL_SOURCE_URI = 'https://www.coffito.gov.br/nsite/?p=33158' as const;
export const ICF_CLASSIFICATION_VERSION = '2026-01' as const;
export const ICF_CLASSIFICATION_LANGUAGE = 'pt' as const;
export const ICF_CLASSIFICATION_SOURCE_URI =
  'https://icd.who.int/browse/2026-01/icf/pt' as const;

type CatalogItem<T extends string> = {
  value: T;
  label: string;
  description: string;
};

const getCatalogLabel = <T extends string>(
  catalog: readonly CatalogItem<T>[],
  value: string,
  fallback: string,
): string => catalog.find((item) => item.value === value)?.label ?? fallback;

export const ASSESSMENT_REPORT_MODE_CATALOG = [
  {
    value: 'assessment_summary',
    label: 'Resumo das avaliações',
    description:
      'Folha de rosto objetiva, adequada para devolutiva ao paciente e leitura rápida dos resultados.',
  },
  {
    value: 'physiotherapy_technical_report',
    label: 'Relatório técnico com diagnóstico fisioterapêutico',
    description:
      'Documento técnico com diagnóstico autoral. Por regra clínica do produto, exige registros manuais na CBDF-1 e na CIF.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportMode = (typeof ASSESSMENT_REPORT_MODE_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_DOMAIN_CATALOG = [
  {
    value: 'exercise_capacity',
    label: 'Capacidade funcional de exercício',
    description: 'Desempenho e resposta fisiológica em testes de exercício.',
  },
  {
    value: 'functional_mobility',
    label: 'Mobilidade e desempenho funcional',
    description: 'Marcha, transferências, mobilidade e desempenho em tarefas funcionais.',
  },
  {
    value: 'peripheral_muscle_strength',
    label: 'Força muscular periférica',
    description: 'Força de preensão, grupos musculares periféricos e testes de carga.',
  },
  {
    value: 'respiratory_muscle_function',
    label: 'Função muscular respiratória',
    description: 'Força, desempenho e resistência da musculatura respiratória.',
  },
  {
    value: 'symptoms_and_control',
    label: 'Sintomas e controle clínico',
    description: 'Sintomas autorreferidos e controle da condição avaliada.',
  },
  {
    value: 'quality_of_life_and_function',
    label: 'Qualidade de vida e impacto funcional',
    description: 'Repercussão da condição sobre atividades, participação e qualidade de vida.',
  },
  {
    value: 'emotional_health',
    label: 'Saúde emocional',
    description: 'Rastreamento de sintomas emocionais relevantes para o cuidado.',
  },
  {
    value: 'sleep_and_recovery',
    label: 'Sono e recuperação',
    description: 'Qualidade do sono, recuperação e repercussões diurnas.',
  },
  {
    value: 'expiratory_flow',
    label: 'Fluxo expiratório',
    description: 'Medidas de fluxo expiratório e comparações próprias do instrumento.',
  },
  {
    value: 'complementary_finding',
    label: 'Achados complementares',
    description: 'Achados que não se enquadram nos demais domínios do relatório.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportDomain = (typeof ASSESSMENT_REPORT_DOMAIN_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_FINDING_STATUS_CATALOG = [
  {
    value: 'within_expected',
    label: 'Dentro do esperado',
    description: 'Resultado compatível com a referência válida registrada pelo instrumento.',
  },
  {
    value: 'borderline',
    label: 'Faixa de atenção',
    description: 'Resultado limítrofe ou que merece acompanhamento segundo a referência registrada.',
  },
  {
    value: 'altered',
    label: 'Alterado',
    description: 'Resultado fora da faixa esperada segundo a referência válida registrada.',
  },
  {
    value: 'not_classified',
    label: 'Sem classificação',
    description: 'O instrumento não forneceu classificação interpretativa válida.',
  },
  {
    value: 'not_interpretable',
    label: 'Não interpretável',
    description: 'Não há base suficiente para interpretar o resultado.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportFindingStatus =
  (typeof ASSESSMENT_REPORT_FINDING_STATUS_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_VALIDITY_STATUS_CATALOG = [
  {
    value: 'valid',
    label: 'Válido',
    description: 'Resultado concluído e utilizável dentro das condições registradas.',
  },
  {
    value: 'limited',
    label: 'Válido com ressalvas',
    description: 'Resultado utilizável, mas com limitação que deve acompanhar sua leitura.',
  },
  {
    value: 'invalid',
    label: 'Inválido',
    description: 'Resultado que não deve sustentar interpretação ou conclusão.',
  },
  {
    value: 'not_assessed',
    label: 'Validade não avaliada',
    description: 'Não houve registro suficiente para determinar a validade.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportValidityStatus =
  (typeof ASSESSMENT_REPORT_VALIDITY_STATUS_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_FINDING_BASIS_CATALOG = [
  {
    value: 'instrument_reference',
    label: 'Referência do instrumento',
    description: 'Classificação já registrada no resultado original do instrumento.',
  },
  {
    value: 'professional_review',
    label: 'Revisão profissional',
    description: 'Classificação explicitamente registrada pelo profissional responsável.',
  },
  {
    value: 'descriptive_only',
    label: 'Sem classificação automática',
    description: 'Resultado apresentado sem classificação clínica.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportFindingBasis =
  (typeof ASSESSMENT_REPORT_FINDING_BASIS_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_AUDIENCE_CATALOG = [
  {
    value: 'patient_or_caregiver',
    label: 'Paciente ou responsável',
    description: 'Devolutiva em linguagem direta para paciente, familiar ou cuidador.',
  },
  {
    value: 'requesting_physician',
    label: 'Médico solicitante',
    description: 'Comunicação dos procedimentos e achados ao médico responsável.',
  },
  {
    value: 'health_professional',
    label: 'Outro profissional de saúde',
    description: 'Encaminhamento ou comunicação interprofissional.',
  },
  {
    value: 'multidisciplinary_team',
    label: 'Equipe multiprofissional',
    description: 'Compartilhamento com a equipe envolvida no cuidado.',
  },
  {
    value: 'other',
    label: 'Outro destinatário',
    description: 'Destinatário definido manualmente pelo profissional.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportAudienceType =
  (typeof ASSESSMENT_REPORT_AUDIENCE_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_PURPOSE_CATALOG = [
  {
    value: 'patient_feedback',
    label: 'Devolutiva das avaliações',
    description: 'Explicar ao paciente ou responsável o que foi avaliado e os principais achados.',
  },
  {
    value: 'clinical_follow_up',
    label: 'Acompanhamento clínico-funcional',
    description: 'Documentar o estado avaliado e apoiar o acompanhamento longitudinal.',
  },
  {
    value: 'referral',
    label: 'Encaminhamento',
    description: 'Compartilhar os achados para continuidade ou complementação do cuidado.',
  },
  {
    value: 'care_transition',
    label: 'Transição de cuidado',
    description: 'Apoiar transferência de responsabilidade ou mudança de serviço.',
  },
  {
    value: 'interprofessional_communication',
    label: 'Comunicação interprofissional',
    description: 'Oferecer uma síntese objetiva a outro profissional ou equipe.',
  },
  {
    value: 'other',
    label: 'Outra finalidade',
    description: 'Finalidade descrita manualmente pelo profissional.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportPurposeType =
  (typeof ASSESSMENT_REPORT_PURPOSE_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_NARRATIVE_FORMAT_CATALOG = [
  {
    value: 'sections',
    label: 'Em seções',
    description:
      'Mantém alterações, resultados esperados e síntese profissional em blocos separados.',
  },
  {
    value: 'continuous_text',
    label: 'Texto corrido',
    description:
      'Reúne os mesmos achados e a síntese profissional em uma única seção narrativa.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportNarrativeFormat =
  (typeof ASSESSMENT_REPORT_NARRATIVE_FORMAT_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_ATTACHMENT_MODE_CATALOG = [
  {
    value: 'cover_only',
    label: 'Somente o relatório-síntese',
    description: 'Exporta apenas a folha de rosto ou relatório técnico.',
  },
  {
    value: 'selected_assessments',
    label: 'Anexar avaliações selecionadas',
    description: 'Inclui os laudos completos explicitamente selecionados.',
  },
  {
    value: 'all_source_assessments',
    label: 'Anexar todas as avaliações-fonte',
    description: 'Inclui os laudos completos de todas as fontes do relatório.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportAttachmentMode =
  (typeof ASSESSMENT_REPORT_ATTACHMENT_MODE_CATALOG)[number]['value'];

export const ASSESSMENT_REPORT_HISTORY_MODE_CATALOG = [
  {
    value: 'none',
    label: 'Sem histórico',
    description: 'Não inclui comparação longitudinal.',
  },
  {
    value: 'selected',
    label: 'Comparações selecionadas',
    description: 'Inclui apenas os resultados históricos escolhidos pelo profissional.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type AssessmentReportHistoryMode =
  (typeof ASSESSMENT_REPORT_HISTORY_MODE_CATALOG)[number]['value'];

export const CBDF_CATEGORY_CATALOG = [
  {
    value: 'S',
    label: 'Saúde cinético-funcional',
    description: 'Capítulos de saúde e risco cinético-funcional.',
  },
  {
    value: 'D',
    label: 'Deficiências cinético-funcionais',
    description: 'Capítulos de deficiências de funções e estruturas relacionadas.',
  },
  {
    value: 'M',
    label: 'Atividades de mobilidade',
    description: 'Capítulos de atividades e desempenho de mobilidade.',
  },
  {
    value: 'P',
    label: 'Participação social',
    description: 'Capítulos de participação em situações de vida.',
  },
] as const satisfies readonly CatalogItem<string>[];

export type CbdfCategory = (typeof CBDF_CATEGORY_CATALOG)[number]['value'];

type CbdfChapterDefinition = {
  code: string;
  category: CbdfCategory;
  label: string;
};

/**
 * Catálogo-base para orientar o registro manual.
 *
 * Estes 36 capítulos não produzem código, diagnóstico ou descrição automaticamente.
 * A seleção apenas organiza a entrada autoral do fisioterapeuta.
 */
export const CBDF_CHAPTER_CATALOG = [
  { code: 'S01', category: 'S', label: 'Saúde cinético-funcional — Sistema Nervoso Periférico' },
  { code: 'S02', category: 'S', label: 'Saúde cinético-funcional — Sistema Nervoso Central' },
  { code: 'S03', category: 'S', label: 'Saúde cinético-funcional — Sistema Musculoesquelético' },
  { code: 'S04', category: 'S', label: 'Saúde cinético-funcional — Sistema Respiratório' },
  { code: 'S05', category: 'S', label: 'Saúde cinético-funcional — Sistema Cardiovascular' },
  { code: 'S06', category: 'S', label: 'Saúde cinético-funcional — Sistema Tegumentar' },
  { code: 'S07', category: 'S', label: 'Saúde cinético-funcional — Sistema Urinário' },
  { code: 'S08', category: 'S', label: 'Saúde cinético-funcional — Sistema Genital' },
  { code: 'S09', category: 'S', label: 'Saúde cinético-funcional — Sistema Digestório' },
  { code: 'S10', category: 'S', label: 'Saúde cinético-funcional — Sistema Metabólico' },
  { code: 'D01', category: 'D', label: 'Deficiência cinético-funcional neuroperiférica' },
  { code: 'D02', category: 'D', label: 'Deficiência cinético-funcional neurocentral' },
  { code: 'D03', category: 'D', label: 'Deficiência cinético-funcional musculoesquelética' },
  { code: 'D04', category: 'D', label: 'Deficiência cinético-funcional respiratória' },
  { code: 'D05', category: 'D', label: 'Deficiência cinético-funcional cardiovascular' },
  { code: 'D06', category: 'D', label: 'Deficiência cinético-funcional tegumentar' },
  { code: 'D07', category: 'D', label: 'Deficiência cinético-funcional urinária' },
  { code: 'D08', category: 'D', label: 'Deficiência cinético-funcional genital' },
  { code: 'D09', category: 'D', label: 'Deficiência cinético-funcional digestória' },
  { code: 'D10', category: 'D', label: 'Deficiência cinético-funcional metabólica' },
  { code: 'M01', category: 'M', label: 'Mobilidade para transferências' },
  { code: 'M02', category: 'M', label: 'Mobilidade para manter-se em posições básicas' },
  { code: 'M03', category: 'M', label: 'Mobilidade para manter-se em posições avançadas' },
  { code: 'M04', category: 'M', label: 'Mobilidade para locomoção/deslocamento básico' },
  { code: 'M05', category: 'M', label: 'Mobilidade para locomoção/deslocamento avançado' },
  { code: 'M06', category: 'M', label: 'Mobilidade para locomoção com cadeira de rodas' },
  { code: 'M07', category: 'M', label: 'Atividades básicas de membro(s) superior(es)' },
  { code: 'M08', category: 'M', label: 'Atividades avançadas de membro(s) superior(es)' },
  { code: 'M09', category: 'M', label: 'Atividades ligadas ao desenvolvimento neuropsicomotor' },
  { code: 'P01', category: 'P', label: 'Participação em atividades de trabalho' },
  { code: 'P02', category: 'P', label: 'Participação em atividades de levantamento de peso' },
  { code: 'P03', category: 'P', label: 'Participação em atividades na posição sentada' },
  { code: 'P04', category: 'P', label: 'Participação em atividades na posição em pé' },
  { code: 'P05', category: 'P', label: 'Participação em atividades esportivas' },
  { code: 'P06', category: 'P', label: 'Participação em atividades recreacionais' },
  { code: 'P07', category: 'P', label: 'Participação em atividades educacionais, lazer, família e comunidade' },
] as const satisfies readonly CbdfChapterDefinition[];

export type CbdfChapterCode = (typeof CBDF_CHAPTER_CATALOG)[number]['code'];

export const ICF_DOMAIN_CATALOG = [
  {
    value: 'body_functions',
    prefix: 'b',
    label: 'Funções do corpo',
    description: 'Funções fisiológicas dos sistemas do corpo.',
  },
  {
    value: 'body_structures',
    prefix: 's',
    label: 'Estruturas do corpo',
    description: 'Partes anatômicas do corpo.',
  },
  {
    value: 'activities_and_participation',
    prefix: 'd',
    label: 'Atividades e participação',
    description: 'Execução de tarefas e envolvimento em situações de vida.',
  },
  {
    value: 'environmental_factors',
    prefix: 'e',
    label: 'Fatores ambientais',
    description: 'Ambiente físico, social e atitudinal como barreira ou facilitador.',
  },
] as const;

export type IcfDomain = (typeof ICF_DOMAIN_CATALOG)[number]['value'];
export type IcfCodePrefix = (typeof ICF_DOMAIN_CATALOG)[number]['prefix'];
export type IcfProblemQualifier = 0 | 1 | 2 | 3 | 4 | 8 | 9;
export type IcfFacilitatorQualifier = '+0' | '+1' | '+2' | '+3' | '+4' | '+8';
export type IcfQualifier = IcfProblemQualifier | IcfFacilitatorQualifier;
export type IcfQualifierContext =
  | 'extent'
  | 'performance'
  | 'capacity'
  | 'barrier'
  | 'facilitator';

export const ICF_PROBLEM_QUALIFIER_CATALOG = [
  { value: 0, label: 'Nenhum problema', description: 'Ausente ou insignificante.' },
  { value: 1, label: 'Problema leve', description: 'Problema de intensidade leve.' },
  { value: 2, label: 'Problema moderado', description: 'Problema de intensidade moderada.' },
  { value: 3, label: 'Problema grave', description: 'Problema de intensidade grave.' },
  { value: 4, label: 'Problema completo', description: 'Problema de intensidade completa.' },
  { value: 8, label: 'Não especificado', description: 'Informação insuficiente para graduar.' },
  { value: 9, label: 'Não aplicável', description: 'O qualificador não se aplica.' },
] as const;

export const ICF_ENVIRONMENTAL_FACILITATOR_CATALOG = [
  { value: '+0', label: 'Nenhum facilitador', description: 'Facilitador ausente ou insignificante.' },
  { value: '+1', label: 'Facilitador leve', description: 'Facilitador de intensidade leve.' },
  { value: '+2', label: 'Facilitador moderado', description: 'Facilitador de intensidade moderada.' },
  { value: '+3', label: 'Facilitador considerável', description: 'Facilitador de intensidade considerável.' },
  { value: '+4', label: 'Facilitador completo', description: 'Facilitador de intensidade completa.' },
  { value: '+8', label: 'Facilitador não especificado', description: 'Informação insuficiente para graduar o facilitador.' },
] as const;

export const getIcfQualifierContextLabel = (context: IcfQualifierContext): string => {
  switch (context) {
    case 'performance':
      return 'Desempenho no ambiente real';
    case 'capacity':
      return 'Capacidade em ambiente padronizado';
    case 'barrier':
      return 'Barreira ambiental';
    case 'facilitator':
      return 'Facilitador ambiental';
    default:
      return 'Extensão do problema';
  }
};

export const isIcfQualifierContext = (value: unknown): value is IcfQualifierContext =>
  value === 'extent'
  || value === 'performance'
  || value === 'capacity'
  || value === 'barrier'
  || value === 'facilitator';

const valuesOf = <T extends string>(
  catalog: readonly CatalogItem<T>[],
): ReadonlySet<string> => new Set(catalog.map((item) => item.value));

const modeValues = valuesOf(ASSESSMENT_REPORT_MODE_CATALOG);
const domainValues = valuesOf(ASSESSMENT_REPORT_DOMAIN_CATALOG);
const findingStatusValues = valuesOf(ASSESSMENT_REPORT_FINDING_STATUS_CATALOG);
const validityStatusValues = valuesOf(ASSESSMENT_REPORT_VALIDITY_STATUS_CATALOG);
const findingBasisValues = valuesOf(ASSESSMENT_REPORT_FINDING_BASIS_CATALOG);
const audienceValues = valuesOf(ASSESSMENT_REPORT_AUDIENCE_CATALOG);
const purposeValues = valuesOf(ASSESSMENT_REPORT_PURPOSE_CATALOG);
const narrativeFormatValues = valuesOf(ASSESSMENT_REPORT_NARRATIVE_FORMAT_CATALOG);
const attachmentModeValues = valuesOf(ASSESSMENT_REPORT_ATTACHMENT_MODE_CATALOG);
const historyModeValues = valuesOf(ASSESSMENT_REPORT_HISTORY_MODE_CATALOG);
const cbdfChapterValues = new Set<string>(CBDF_CHAPTER_CATALOG.map((item) => item.code));
const icfDomainValues = new Set<string>(ICF_DOMAIN_CATALOG.map((item) => item.value));

export const isAssessmentReportMode = (value: unknown): value is AssessmentReportMode =>
  typeof value === 'string' && modeValues.has(value);

export const isAssessmentReportDomain = (value: unknown): value is AssessmentReportDomain =>
  typeof value === 'string' && domainValues.has(value);

export const isAssessmentReportFindingStatus = (
  value: unknown,
): value is AssessmentReportFindingStatus =>
  typeof value === 'string' && findingStatusValues.has(value);

export const isAssessmentReportValidityStatus = (
  value: unknown,
): value is AssessmentReportValidityStatus =>
  typeof value === 'string' && validityStatusValues.has(value);

export const isAssessmentReportFindingBasis = (
  value: unknown,
): value is AssessmentReportFindingBasis =>
  typeof value === 'string' && findingBasisValues.has(value);

export const isAssessmentReportAudienceType = (
  value: unknown,
): value is AssessmentReportAudienceType =>
  typeof value === 'string' && audienceValues.has(value);

export const isAssessmentReportPurposeType = (
  value: unknown,
): value is AssessmentReportPurposeType =>
  typeof value === 'string' && purposeValues.has(value);

export const isAssessmentReportNarrativeFormat = (
  value: unknown,
): value is AssessmentReportNarrativeFormat =>
  typeof value === 'string' && narrativeFormatValues.has(value);

export const isAssessmentReportAttachmentMode = (
  value: unknown,
): value is AssessmentReportAttachmentMode =>
  typeof value === 'string' && attachmentModeValues.has(value);

export const isAssessmentReportHistoryMode = (
  value: unknown,
): value is AssessmentReportHistoryMode =>
  typeof value === 'string' && historyModeValues.has(value);

export const isCbdfChapterCode = (value: unknown): value is CbdfChapterCode =>
  typeof value === 'string' && cbdfChapterValues.has(value);

export const isIcfDomain = (value: unknown): value is IcfDomain =>
  typeof value === 'string' && icfDomainValues.has(value);

export const isIcfProblemQualifier = (value: unknown): value is IcfProblemQualifier =>
  value === 0 || value === 1 || value === 2 || value === 3 || value === 4 || value === 8 || value === 9;

export const isIcfFacilitatorQualifier = (value: unknown): value is IcfFacilitatorQualifier =>
  value === '+0'
  || value === '+1'
  || value === '+2'
  || value === '+3'
  || value === '+4'
  || value === '+8';

export const isIcfQualifier = (value: unknown): value is IcfQualifier =>
  isIcfProblemQualifier(value) || isIcfFacilitatorQualifier(value);

export const getAssessmentReportModeLabel = (mode: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_MODE_CATALOG, mode, 'Relatório de avaliações');

export const getAssessmentReportDomainLabel = (domain: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_DOMAIN_CATALOG, domain, 'Achados complementares');

export const getAssessmentReportFindingStatusLabel = (status: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_FINDING_STATUS_CATALOG, status, 'Sem classificação');

export const getAssessmentReportValidityStatusLabel = (status: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_VALIDITY_STATUS_CATALOG, status, 'Validade não avaliada');

export const getAssessmentReportAudienceLabel = (audience: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_AUDIENCE_CATALOG, audience, 'Destinatário não informado');

export const getAssessmentReportPurposeLabel = (purpose: string): string =>
  getCatalogLabel(ASSESSMENT_REPORT_PURPOSE_CATALOG, purpose, 'Finalidade não informada');

export const getCbdfChapter = (code: string) =>
  CBDF_CHAPTER_CATALOG.find((chapter) => chapter.code === code) ?? null;

export const getCbdfChapterLabel = (code: string): string =>
  getCbdfChapter(code)?.label ?? 'Capítulo CBDF não identificado';

export const normalizeCbdfCode = (code: string): string =>
  code.trim().replace(/\s+/g, ' ');

/**
 * Valida somente a estrutura do código transcrito e a correspondência com o
 * capítulo-base. A existência do código não é consultada no catálogo CBDF.
 */
export const isCbdfFullCodeForChapter = (
  value: unknown,
  chapterCode: CbdfChapterCode,
): value is string => {
  if (typeof value !== 'string') return false;
  const match = normalizeCbdfCode(value).match(/^([A-Z]\d{2})\.\d{2}(?:\.\d){4}$/i);
  return match?.[1].toUpperCase() === chapterCode.toUpperCase();
};

export const getIcfDomainByCode = (code: string) => {
  const prefix = code.trim().toLowerCase().slice(0, 1);
  return ICF_DOMAIN_CATALOG.find((domain) => domain.prefix === prefix) ?? null;
};

export const normalizeIcfCode = (code: string): string => code.trim().toLowerCase();

export const isIcfCode = (value: unknown): value is string =>
  typeof value === 'string'
  && /^(?:[bs][1-8](?:\d{2,4})?|d[1-9](?:\d{2,4})?|e[1-5](?:\d{2,4})?)$/i.test(value.trim());

export const isIcfCodeForDomain = (code: unknown, domain: IcfDomain): code is string => {
  if (!isIcfCode(code)) return false;
  return getIcfDomainByCode(code)?.value === domain;
};

export const getIcfQualifierLabel = (
  qualifier: IcfQualifier,
  domain?: IcfDomain,
): string => {
  if (isIcfFacilitatorQualifier(qualifier)) {
    return ICF_ENVIRONMENTAL_FACILITATOR_CATALOG.find((item) => item.value === qualifier)?.label
      ?? 'Facilitador não identificado';
  }

  const baseLabel = ICF_PROBLEM_QUALIFIER_CATALOG.find((item) => item.value === qualifier)?.label
    ?? 'Qualificador não identificado';

  if (domain !== 'environmental_factors' || qualifier === 8 || qualifier === 9) return baseLabel;
  if (qualifier === 0) return 'Nenhuma barreira';
  if (qualifier === 1) return 'Barreira leve';
  if (qualifier === 2) return 'Barreira moderada';
  if (qualifier === 3) return 'Barreira grave';
  return 'Barreira completa';
};

export const formatIcfQualifiedCode = (
  code: string,
  qualifier: IcfQualifier,
  capacityQualifier: IcfProblemQualifier | null = null,
): string =>
  normalizeIcfCode(code).startsWith('d') && isIcfProblemQualifier(qualifier)
    ? `${normalizeIcfCode(code)}.${qualifier}${capacityQualifier ?? ''}`
    : isIcfFacilitatorQualifier(qualifier)
      ? `${normalizeIcfCode(code)}${qualifier}`
      : `${normalizeIcfCode(code)}.${qualifier}`;
